import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, uploadFile } from "../client";

// ==================== Types ====================

export type CampaignStatus =
  | "pending"
  | "analyzing"
  | "analyzed"
  | "matching"
  | "enriching"
  | "paused"
  | "creating"
  | "completed"
  | "failed"
  | "cancelled";

export interface ImportCampaign {
  id: string;
  name: string;
  description: string | null;
  status: CampaignStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  analysisStartedAt: string | null;
  processingCompletedAt: string | null;
  file: {
    name: string;
    size: number;
  } | null;
  counts: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    biblioGroups: number;
    createdRecords: number;
    createdItems: number;
  };
}

export interface ImportProgress {
  status: string;
  progress: number;
  totalRows: number;
  processedRows: number;
  matchedRows: number;
  enrichedRows: number;
  biblioGroups: {
    total: number;
    processed: number;
    pending: number;
  };
  errors: number;
}

export interface StagingRow {
  id: string;
  lineNumber: number;
  status: string;
  legacyNoticeId: string | null;
  barcode: string | null;
  title: string | null;
  author: string | null;
  callNumber: string | null;
  location: string | null;
  itemStatus: string | null;
  errors: string[];
}

export interface BiblioGroup {
  id: string;
  legacyNoticeId: string;
  title: string | null;
  author: string | null;
  publicationYear: number | null;
  rowCount: number;
  catalogMatch: {
    recordId: string | null;
    confidence: string | null;
    confirmed: boolean;
  };
  bnfEnrichment: {
    done: boolean;
    workArk: string | null;
    confidence: string | null;
  };
  needsReview: boolean;
}

export interface CampaignStats {
  campaign: {
    id: string;
    status: CampaignStatus;
  };
  stats: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    processedRows: number;
    matchedRows: number;
    enrichedRows: number;
    biblioGroups: number;
    createdRecords: number;
    updatedRecords: number;
    createdItems: number;
  };
  progress: ImportProgress;
}

// ==================== Query Keys ====================

const CAMPAIGNS_KEY = "import-campaigns";
const CAMPAIGN_KEY = "import-campaign";
const CAMPAIGN_PREVIEW_KEY = "import-campaign-preview";
const CAMPAIGN_GROUPS_KEY = "import-campaign-groups";
const CAMPAIGN_STATS_KEY = "import-campaign-stats";

// ==================== Hooks ====================

/**
 * List import campaigns.
 */
export function useImportCampaigns(params?: {
  status?: CampaignStatus;
  limit?: number;
  offset?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.set("status", params.status);
  if (params?.limit) queryParams.set("limit", String(params.limit));
  if (params?.offset) queryParams.set("offset", String(params.offset));

  const queryString = queryParams.toString();

  return useQuery({
    queryKey: [CAMPAIGNS_KEY, queryString],
    queryFn: () =>
      get<{
        campaigns: ImportCampaign[];
        total: number;
        limit: number;
        offset: number;
      }>(`/api/import/campaigns${queryString ? `?${queryString}` : ""}`),
  });
}

/**
 * Get a single campaign with progress.
 */
export function useImportCampaign(id: string | undefined) {
  return useQuery({
    queryKey: [CAMPAIGN_KEY, id],
    queryFn: () =>
      get<{
        campaign: ImportCampaign;
        progress: ImportProgress;
      }>("/import/campaigns/" + (id ?? "")),
    enabled: !!id,
    refetchInterval: (query) => {
      // Auto-refresh while processing
      const status = query.state.data?.campaign.status;
      if (status && ["analyzing", "matching", "enriching", "creating"].includes(status)) {
        return 2000; // 2 seconds
      }
      return false;
    },
  });
}

/**
 * Get campaign statistics.
 */
export function useImportCampaignStats(id: string | undefined) {
  return useQuery({
    queryKey: [CAMPAIGN_STATS_KEY, id],
    queryFn: () => get<CampaignStats>("/import/campaigns/" + (id ?? "") + "/stats"),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.campaign.status;
      if (status && ["analyzing", "matching", "enriching", "creating"].includes(status)) {
        return 2000;
      }
      return false;
    },
  });
}

/**
 * Get staging rows preview.
 */
export function useImportPreview(
  campaignId: string | undefined,
  params?: { limit?: number; offset?: number; status?: string }
) {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.set("limit", String(params.limit));
  if (params?.offset) queryParams.set("offset", String(params.offset));
  if (params?.status) queryParams.set("status", params.status);

  const queryString = queryParams.toString();

  return useQuery({
    queryKey: [CAMPAIGN_PREVIEW_KEY, campaignId, queryString],
    queryFn: () =>
      get<{
        rows: StagingRow[];
        total: number;
        limit: number;
        offset: number;
      }>(
        "/import/campaigns/" +
          (campaignId ?? "") +
          "/preview" +
          (queryString ? "?" + queryString : "")
      ),
    enabled: !!campaignId,
  });
}

/**
 * Get bibliographic groups.
 */
export function useImportGroups(
  campaignId: string | undefined,
  params?: { limit?: number; offset?: number; needsReview?: boolean }
) {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.set("limit", String(params.limit));
  if (params?.offset) queryParams.set("offset", String(params.offset));
  if (params?.needsReview) queryParams.set("needsReview", "true");

  const queryString = queryParams.toString();

  return useQuery({
    queryKey: [CAMPAIGN_GROUPS_KEY, campaignId, queryString],
    queryFn: () =>
      get<{
        groups: BiblioGroup[];
        total: number;
        limit: number;
        offset: number;
      }>(
        "/import/campaigns/" +
          (campaignId ?? "") +
          "/groups" +
          (queryString ? "?" + queryString : "")
      ),
    enabled: !!campaignId,
  });
}

/**
 * Create a new import campaign.
 */
export function useCreateImportCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      targetLocationId?: string;
      options?: Record<string, unknown>;
    }) => post<{ campaign: ImportCampaign }>("/import/campaigns", data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
    },
  });
}

/**
 * Upload a file to a campaign.
 */
export function useUploadImportFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, file }: { campaignId: string; file: File }) =>
      uploadFile<{ campaign: ImportCampaign; message: string }>(
        "/import/campaigns/" + campaignId + "/upload",
        file
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGN_KEY, variables.campaignId] });
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
    },
  });
}

/**
 * Confirm or reject a catalog match for a group.
 */
export function useConfirmMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      groupId,
      confirmed,
    }: {
      campaignId: string;
      groupId: string;
      confirmed: boolean;
    }) =>
      post<{ group: { id: string; matchConfirmed: boolean } }>(
        "/import/campaigns/" + campaignId + "/groups/" + groupId + "/confirm",
        { confirmed }
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGN_GROUPS_KEY, variables.campaignId] });
    },
  });
}

/**
 * Start the matching and enrichment process.
 */
export function useProcessCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) =>
      post<{ campaign: ImportCampaign; message: string }>(
        "/import/campaigns/" + campaignId + "/process",
        {}
      ),
    onSuccess: (_, campaignId) => {
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGN_KEY, campaignId] });
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
    },
  });
}

/**
 * Start entity creation.
 */
export function useCreateEntities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) =>
      post<
        | { campaign: ImportCampaign; message: string }
        | { error: string; groupsNeedingReview: number }
      >("/import/campaigns/" + campaignId + "/create", {}),
    onSuccess: (_, campaignId) => {
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGN_KEY, campaignId] });
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
    },
  });
}

/**
 * Cancel a campaign.
 */
export function useCancelCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) =>
      post<{ campaign: ImportCampaign; message: string }>(
        "/import/campaigns/" + campaignId + "/cancel",
        {}
      ),
    onSuccess: (_, campaignId) => {
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGN_KEY, campaignId] });
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY] });
    },
  });
}
